<template>
  <div class="language-settings-container">
    <!-- Header Section -->
    <div class="settings-header">
      <div class="header-content">
        <h1 class="settings-title">
          <v-icon class="title-icon" color="#fff">mdi-translate</v-icon>
          {{ $t("language_settings_title") }}
        </h1>
        <p class="settings-subtitle">
          Customize your language and regional preferences
        </p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <v-progress-circular 
        indeterminate 
        color="primary" 
        size="48"
        width="4"
      />
      <p class="loading-text">Loading your preferences...</p>
    </div>

    <!-- Settings Cards -->
    <div v-else class="settings-grid">
      <v-card
        v-for="item in menuList"
        :key="item.key"
        class="setting-card"
        elevation="2"
        :class="{ 'setting-card--active': item.expanded }"
      >
        <!-- Card Header -->
        <v-card-title class="setting-card-header">
          <div class="setting-icon-wrapper">
            <v-icon :color="item.iconColor" size="24">
              {{ item.icon }}
            </v-icon>
          </div>
          <div class="setting-header-content">
            <h3 class="setting-title">{{ item.title }}</h3>
            <p class="setting-description">{{ item.description }}</p>
          </div>
          <v-btn
            icon
            small
            @click="item.expanded = !item.expanded"
            class="expand-btn"
          >
            <v-icon>
              {{ item.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <!-- Expandable Content -->
        <v-expand-transition>
          <div v-show="item.expanded" class="setting-content">
            <v-divider class="setting-divider" />
            
            <div class="setting-form">
              <!-- Current Selection Display -->
              <div class="current-selection">
                <span class="current-label">Current:</span>
                <v-chip 
                  small 
                  :color="item.chipColor" 
                  text-color="white"
                  class="current-chip"
                >
                  <v-icon left small>{{ item.valueIcon }}</v-icon>
                  {{ getDisplayValue(item) }}
                </v-chip>
              </div>

              <!-- Selection Input -->
              <div class="selection-input">
                <v-autocomplete
                  v-if="item.key === 'timezone'"
                  :items="item.list"
                  v-model="item.value"
                  :filter="searchFilter"
                  item-text="name"
                  item-value="name"
                  @input="onChange(item)"
                  :label="`Select ${item.title}`"
                  outlined
                  dense
                  clearable
                  :prepend-inner-icon="item.icon"
                  class="selection-autocomplete"
                  :search-input.sync="timezoneSearch"
                  no-data-text="No timezone found"
                >
                  <template v-slot:selection="data">
                    <span class="timezone-selection">
                      UTC{{ data.item.currentTimeFormat }} - {{ data.item.name }}
                    </span>
                  </template>
                  
                  <template v-slot:item="data">
                    <v-list-item-content>
                      <v-list-item-title>
                        <span class="timezone-name">{{ data.item.name }}</span>
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <span class="timezone-offset">UTC{{ data.item.currentTimeFormat }}</span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>

                <v-autocomplete
                  v-else
                  :items="item.list"
                  v-model="item.value"
                  @input="onChange(item)"
                  :label="`Select ${item.title}`"
                  outlined
                  dense
                  clearable
                  :prepend-inner-icon="item.icon"
                  class="selection-autocomplete"
                />
              </div>

              <!-- Save Confirmation -->
              <v-fade-transition>
                <v-alert
                  v-if="item.showSaved"
                  type="success"
                  dense
                  text
                  class="save-alert"
                  dismissible
                >
                  <v-icon slot="prepend" small>mdi-check-circle</v-icon>
                  {{ item.title }} updated successfully!
                </v-alert>
              </v-fade-transition>
            </div>
          </div>
        </v-expand-transition>
      </v-card>
    </div>

    <!-- Footer Info -->
    <div class="settings-footer">
      <v-divider class="footer-divider" />
      <div class="footer-content">
        <v-icon small color="#ffff">mdi-information-outline</v-icon>
        <span class="footer-text">
          Changes are saved automatically and will take effect immediately.
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { getTimeZones } from "@vvo/tzdb";

export default {
  name: "EnhancedLanguageSettings",
  
  data() {
    return {
      menuList: [
        {
          title: "Language",
          description: "Language of title, text of aKinder™ Volunteer",
          key: "language",
          list: ["English", "Spanish", "French", "German", "Italian", "Portuguese"],
          icon: "mdi-translate",
          iconColor: "primary",
          chipColor: "primary",
          valueIcon: "mdi-earth",
          expanded: false,
          showSaved: false
        },
        {
          title: "Timezone",
          description: "Please select your timezone for accurate time display",
          key: "timezone",
          list: getTimeZones(),
          icon: "mdi-clock-outline",
          iconColor: "secondary",
          chipColor: "secondary",
          valueIcon: "mdi-map-marker",
          expanded: false,
          showSaved: false
        }
      ],
      loading: false,
      timezoneSearch: ""
    };
  },

  methods: {
    ...mapActions("usersetting", {
      getSettings: "getRegionSettings",
      setSetting: "setRegionSetting"
    }),

    onChange(item) {
      this.setSetting({ key: item.key, value: item.value });
      
      // Show save confirmation
      item.showSaved = true;
      setTimeout(() => {
        item.showSaved = false;
      }, 3000);
    },

    searchFilter(item, queryText) {
      const searchText = queryText.toLowerCase();
      
      // Search in timezone name
      if (item.name && item.name.toLowerCase().includes(searchText)) {
        return true;
      }
      
      // Search in timezone offset
      if (item.currentTimeFormat && item.currentTimeFormat.toLowerCase().includes(searchText)) {
        return true;
      }
      
      return false;
    },

    getDisplayValue(item) {
      if (item.key === "timezone" && item.value) {
        return typeof item.value === 'object' ? item.value.name : item.value;
      }
      return item.value || `Select ${item.title}`;
    },

    // Auto-expand first card on mount
    initializeExpansion() {
      if (this.menuList.length > 0) {
        this.menuList[0].expanded = true;
      }
    }
  },

  computed: {
    ...mapGetters({
      language: "usersetting/getLanguage"
    })
  },

  watch: {
    language(newValue) {
      if (newValue) {
        this.$i18n.locale = newValue;
      }
    }
  },

  mounted() {
    this.loading = true;
    
    this.getSettings()
      .then(res => {
        this.loading = false;
        
        // Map settings to menuList
        res.slice(0, this.menuList.length).forEach((r, index) => {
          if (this.menuList[index].key === "timezone") {
            const tzObj = this.menuList[index].list.find(tz => tz.name === r);
            this.menuList[index].value = tzObj || r;
          } else {
            this.menuList[index].value = r;
          }
        });
        
        // Initialize expansion after data is loaded
        this.initializeExpansion();
      })
      .catch(error => {
        this.loading = false;
        console.error("Error loading settings:", error);
      });
  }
};
</script>

<style scoped>
.language-settings-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 16px;
  min-height: 100vh;
  /* background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); */
}

/* Header Styles */
.settings-header {
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 32px;
  color: white;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.header-content {
  text-align: center;
}

.settings-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.title-icon {
  font-size: 3rem !important;
}

.settings-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0;
}

/* Loading Styles */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.loading-text {
  margin-top: 16px;
  font-size: 1.1rem;
  color: #666;
}

/* Settings Grid */
.settings-grid {
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr;
}

/* Card Styles */
.setting-card {
  border-radius: 16px !important;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid transparent;
}

.setting-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}

.setting-card--active {
  border-color: #084468;
  box-shadow: 0 8px 32px rgba(3, 12, 142, 0.2) !important;
}

.setting-card-header {
  padding: 24px !important;
  background: linear-gradient(135deg, #f8f9ff 0%, #f1f3ff 100%);
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.setting-card-header:hover {
  background: linear-gradient(135deg, #f1f3ff 0%, #e8ecff 100%);
}

.setting-icon-wrapper {
  background: white;
  border-radius: 12px;
  padding: 12px;
  margin-right: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.setting-header-content {
  flex: 1;
}

.setting-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 4px;
  color: #2c3e50;
}

.setting-description {
  color: #64748b;
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.4;
}

.expand-btn {
  background: rgba(102, 126, 234, 0.1) !important;
  border-radius: 8px !important;
}

/* Content Styles */
.setting-content {
  padding: 0 24px 24px;
}

.setting-divider {
  margin: 0 0 24px 0 !important;
}

.setting-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.current-selection {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #084468;
}

.current-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.current-chip {
  font-weight: 500;
}

.selection-input {
  flex: 1;
}

.selection-autocomplete {
  margin-top: 8px;
}

.timezone-selection {
  font-size: 0.9rem;
  color: #495057;
}

.timezone-name {
  font-weight: 500;
  color: #2c3e50;
}

.timezone-offset {
  font-family: 'Courier New', monospace;
  color: #6c757d;
  font-size: 0.85rem;
}

.save-alert {
  margin-top: 16px;
  border-radius: 8px;
}

/* Footer Styles */
.settings-footer {
  margin-top: 48px;
  padding: 24px 0;
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
}

.footer-divider {
  margin-bottom: 16px !important;
  color: #ffff;
}

.footer-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #ffff;
}

.footer-text {
  font-size: 0.9rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .language-settings-container {
    padding: 12px;
  }

  .settings-header {
    padding: 24px 16px;
    margin-bottom: 24px;
  }

  .settings-title {
    font-size: 2rem;
    flex-direction: column;
    gap: 12px;
  }

  .title-icon {
    font-size: 2.5rem !important;
  }

  .settings-subtitle {
    font-size: 1rem;
  }

  .setting-card-header {
    padding: 20px 16px !important;
  }

  .setting-icon-wrapper {
    padding: 10px;
    margin-right: 12px;
  }

  .setting-title {
    font-size: 1.1rem;
  }

  .setting-description {
    font-size: 0.85rem;
  }

  .setting-content {
    padding: 0 16px 20px;
  }

  .current-selection {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

@media (max-width: 480px) {
  .settings-header {
    padding: 20px 12px;
  }

  .settings-title {
    font-size: 1.8rem;
  }

  .setting-card-header {
    padding: 16px 12px !important;
  }

  .setting-content {
    padding: 0 12px 16px;
  }

  .current-selection {
    padding: 12px;
  }
}

/* Dark theme support */
/* @media (prefers-color-scheme: dark) {
  .language-settings-container {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    color: white;
  }

  .setting-card {
    background: #2c3e50 !important;
    color: white;
  }

  .setting-card-header {
    background: linear-gradient(135deg, #34495e 0%, #2c3e50 100%);
  }

  .setting-title {
    color: white;
  }

  .setting-description {
    color: #bdc3c7;
  }

  .current-selection {
    background: #34495e;
    border-left-color: #3498db;
  }

  .current-label {
    color: #ecf0f1;
  }

  .timezone-name {
    color: #ecf0f1;
  }

  .footer-content {
    color: #95a5a6;
  }
} */
</style>

